import React, { useState } from 'react';

import type { MultiSelectOption, SelectOption } from '@rtkwlf/fenrir-react';
import { HStack, MultiSelect, Select, VStack } from '@rtkwlf/fenrir-react';
import type {
  OrganizationPreferenceResponseBody,
  SubjectTemplates,
  SubjectTemplatesBodyTemplatesInner,
} from '@rtkwlf/notification-service';
import { useTranslation } from 'react-i18next';

import { FormGroup, Typography } from '@packages/fenrir/components';

interface SubjectLineCustomizationDropdownProps {
  templates?: SubjectTemplatesBodyTemplatesInner[];
  preferences?: OrganizationPreferenceResponseBody;
  onSuffixChange: (subjectSuffix: SubjectTemplates[] | undefined) => void;
  onPrefixChange: (subjectPrefix: SubjectTemplates | undefined) => void;
  suffixHelperText?: string;
}

const SubjectLineCustomizationDropdown = ({
  preferences,
  templates,
  onPrefixChange,
  onSuffixChange,
  suffixHelperText,
}: SubjectLineCustomizationDropdownProps) => {
  const { t } = useTranslation('notifications');

  const options: SelectOption[] = Array.isArray(templates)
    ? templates.map((templateOption: SubjectTemplatesBodyTemplatesInner) => ({
        text: templateOption.description || '',
        value: templateOption.template || '',
      }))
    : [];

  const defaultPrefixOption = preferences?.subjectPrefix || '';

  const multiSelectOptions: MultiSelectOption[] = Array.isArray(templates)
    ? templates.map((templateOption: SubjectTemplatesBodyTemplatesInner) => ({
        label: templateOption.description || '',
        value: templateOption.template || '',
      }))
    : [];

  const defaultMultiSelectValues = Array.isArray(preferences?.subjectSuffixes)
    ? multiSelectOptions.filter((option) =>
        preferences?.subjectSuffixes?.some((suffix) => suffix === option.value),
      )
    : [];

  const [defaultPrefix, setDefaultPrefix] =
    useState<string>(defaultPrefixOption);

  const [selectedOptions, setSelectedOptions] = useState<MultiSelectOption[]>(
    defaultMultiSelectValues,
  );

  const onPrefixSelect = (option: SelectOption | null) => {
    if (option) {
      setDefaultPrefix(option.value);
      const newPrefix = option.value as SubjectTemplates;
      onPrefixChange(newPrefix);
    } else {
      setDefaultPrefix('');
      onPrefixChange(undefined);
    }
  };

  const onSuffixSelect = (selectedOptions: MultiSelectOption[]) => {
    setSelectedOptions(selectedOptions);
    const newSuffixArray = selectedOptions
      .map((option) => option.value as SubjectTemplates | undefined)
      .filter((value): value is SubjectTemplates => value !== undefined);
    if (newSuffixArray.length > 0) {
      onSuffixChange(newSuffixArray);
    } else {
      onSuffixChange(undefined);
    }
  };

  return (
    <FormGroup>
      <Typography variant="h5" gutterBottom>
        {t('subjectLineCustomization.title')}
      </Typography>
      <Typography gutterBottom variant="caption" color="textDisabled">
        {t('subjectLineCustomization.description')}
      </Typography>
      <HStack alignItems="flex-start" gap="default" width="full">
        <VStack
          width="16rem"
          gap="zero"
          margin="zero"
          marginTop={suffixHelperText ? 'medium' : 'zero'}
          position="relative"
          flex="none"
        >
          <Typography variant="caption" color="textDisabled">
            {t('subjectLineCustomization.prefixLabel')}
          </Typography>
          <Select
            options={options}
            value={defaultPrefix}
            onSelection={onPrefixSelect}
            placeholder={t('subjectLineCustomization.placeholderText')}
          />
        </VStack>
        <VStack
          gap="zero"
          width="full"
          position="relative"
          style={
            suffixHelperText ? { height: 'auto', paddingTop: '1.5em' } : {}
          }
        >
          <Typography variant="caption" color="textDisabled">
            {t('subjectLineCustomization.suffixLabel')}
          </Typography>
          <MultiSelect
            options={multiSelectOptions}
            placeholder={
              selectedOptions.length !== 0
                ? ''
                : t('subjectLineCustomization.placeholderText')
            }
            defaultOptionsSelected={defaultMultiSelectValues}
            onSelectedOptionsChange={onSuffixSelect}
          />
          {suffixHelperText && (
            <Typography
              variant="caption"
              color="textDisabled"
              mt="small"
              style={{
                height: 'auto',
                position: 'absolute',
                top: '80%',
                zIndex: 1,
              }}
            >
              {suffixHelperText}
            </Typography>
          )}
        </VStack>
      </HStack>
    </FormGroup>
  );
};

export default SubjectLineCustomizationDropdown;
